'use client';

import React, { useState, useEffect } from 'react';
import Link from '@/core/i18n/navigation';
import Script from 'next/script';
import { ShieldCheck, X } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { cn } from '@/core/utils/cn';
import { useConsent } from '@/core/components/providers/ConsentProvider';
import { useTranslation } from '@/core/hooks/useTranslation';

export function CookieConsent({ config }: { config?: any }) {
  const { consent, setConsent, isLoading } = useConsent();
  // Starts visible and is rendered on the server: the banner used to appear only 1.5 s after
  // hydration (plus a 0.7 s fade), and on pages without a big image its text was the LCP element
  // (~5.6 s on mobile in PageSpeed — zojahandmade /sklep, czasmuzyki /oferta-koncertowa). For a
  // visitor who already chose, the inline script in cookieBannerScript.ts hides this
  // server-rendered copy before first paint (data-consent-pending), and the effect below unmounts it.
  const [shouldShow, setShouldShow] = useState(true);
  const { t } = useTranslation();

  const provider = config?.provider || (config?.cookiebot_id ? 'cookiebot' : 'native');
  const cookiebotId = config?.cookiebot_id;

  // Default config if none provided
  const cookie = config || {
    enabled: true,
    bg_color: '#ffffff',
    text_color: '#111827',
    border_radius: 16,
    border_width: 0,
    border_color: '#e5e7eb',
    shadow: true,
    padding: 24,
    font_size: 11,
    font_weight: 'medium',
    accept_button: {
      label: '',
      bg_color: 'primary',
      text_color: '#ffffff',
      border_radius: 10,
      font_size: 10,
      font_weight: 'bold',
      border_width: 0,
      border_color: 'primary'
    },
    decline_button: {
      label: '',
      bg_color: '#f3f4f6',
      text_color: '#374151',
      border_radius: 10,
      font_size: 10,
      font_weight: 'bold',
      border_width: 1,
      border_color: '#d1d5db'
    }
  };

  useEffect(() => {
    if (provider === 'cookiebot' && cookiebotId) {
      const handleCookiebotConsent = () => {
        if (window.Cookiebot?.consent) {
          const { statistics, marketing } = window.Cookiebot.consent;
          // 'all' grants every Consent Mode signal (analytics_storage AND ad_storage/ad_user_data/
          // ad_personalization) and loads GA4, Ads and the Pixel together — so it needs both
          // categories; statistics alone must not switch the advertising signals on.
          setConsent(statistics && marketing ? 'all' : 'necessary');
        }
      };

      // OnConsentReady covers the stored/first choice; OnAccept/OnDecline a change made later
      // from the Cookiebot "change consent" dialog, which previously left the old level in place.
      const events = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'];
      events.forEach((name) => window.addEventListener(name, handleCookiebotConsent));
      return () => {
        events.forEach((name) => window.removeEventListener(name, handleCookiebotConsent));
      };
    }
  }, [provider, cookiebotId, setConsent]);

  useEffect(() => {
    if (!cookie.enabled || isLoading || provider !== 'native') return;
    
    // Only show if consent not determined
    setShouldShow(consent === null);
  }, [cookie.enabled, consent, isLoading, provider]);

  const handleConsent = (type: 'all' | 'necessary') => {
    setConsent(type);
    setShouldShow(false);
  };

  // If Cookiebot provider is active, load Cookiebot script and hide native ShopStag banner
  if (provider === 'cookiebot' && cookiebotId) {
    return (
      <Script
        id="Cookiebot"
        src="https://consent.cookiebot.com/uc.js"
        data-cbid={cookiebotId}
        data-blockingmode="auto"
        type="text/javascript"
        strategy="afterInteractive"
      />
    );
  }

  if (!shouldShow || !cookie.enabled || provider === 'custom') return null;

  const getButtonStyle = (btn: any) => ({
    backgroundColor: btn.bg_color === 'primary' ? 'var(--primary)' : btn.bg_color,
    color: btn.text_color,
    borderRadius: `${btn.border_radius}px`,
    fontSize: `${btn.font_size}px`,
    fontWeight: btn.font_weight,
    border: btn.border_width > 0 ? `${btn.border_width}px solid ${btn.border_color === 'primary' ? 'var(--primary)' : btn.border_color}` : 'none',
  });

  // The admin settings form (constants.ts DEFAULT_COOKIE / normalizeCookieConfig) used to seed
  // these exact Polish strings into every tenant's saved cookie_config, even for merchants who
  // never touched the button labels — permanently overriding the translated `t()` fallback below
  // for every locale/domain. Treat those known legacy literals the same as "never customized" so
  // already-persisted configs self-heal; a merchant who genuinely typed something else (in any
  // language) still has that exact text respected verbatim, same as before.
  const isLegacyDefaultLabel = (label: string | undefined, ...legacyLiterals: string[]) =>
    !label || legacyLiterals.includes(label);

  const acceptLabel = isLegacyDefaultLabel(cookie.accept_button?.label, 'Akceptuję', 'Akceptuj Wszystkie')
    ? t('cookie.accept_all', 'Zaakceptuj')
    : cookie.accept_button.label;
  const declineLabel = isLegacyDefaultLabel(cookie.decline_button?.label, 'Tylko niezbędne')
    ? t('cookie.necessary_only', 'Tylko niezbędne')
    : cookie.decline_button.label;
  const cookieTitle = cookie.title || cookie.heading || t('cookie.title', 'Pliki cookies i prywatność');
  const cookieDescription = cookie.description || cookie.text || cookie.message || (
    config?.gtm_id || config?.google_analytics_id || config?.fb_pixel_id || config?.livesession_id
      ? t('cookie.desc_marketing')
      : t('cookie.desc_default')
  );

  return (
    <div 
      className="cookie-consent-banner fixed bottom-0 left-0 right-0 z-[100] p-4 md:bottom-6 md:right-6 md:left-auto md:max-w-sm animate-in fade-in slide-in-from-bottom-5 duration-700 pointer-events-none"
      data-consent-pending={isLoading ? '' : undefined}
      style={{ contain: 'layout paint', willChange: 'transform, opacity' }}
    >
      <div 
        className={cn(
            "backdrop-blur-md relative overflow-hidden group pointer-events-auto",
            cookie.shadow ? "shadow-2xl" : ""
        )}
        style={{
            backgroundColor: cookie.bg_color === 'primary' ? 'var(--primary)' : `${cookie.bg_color}F2`,
            borderRadius: `${cookie.border_radius}px`,
            border: cookie.border_width > 0 ? `${cookie.border_width}px solid ${cookie.border_color === 'primary' ? 'var(--primary)' : cookie.border_color}` : '1px solid rgba(0,0,0,0.05)',
            padding: `${cookie.padding}px`,
            color: cookie.text_color,
            fontFamily: cookie.font_family || undefined,
            letterSpacing: cookie.letter_spacing || undefined,
        }}
      >
        <div className="absolute -right-4 -top-4 w-20 h-20 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-colors" />
        
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-sm font-black uppercase tracking-tight" style={{ color: cookie.text_color }}>
                {cookieTitle}
            </h3>
            <p 
                // No `italic`: on shops whose theme font is a Google font with an italic axis
                // (Nunito Sans, Plus Jakarta Sans…) this one line made every first visit download
                // the italic font files too (~50–60 KB, competing with the LCP image).
                className="leading-relaxed mt-1 opacity-70"
                style={{ fontSize: `${cookie.font_size}px`, fontWeight: cookie.font_weight }}
            >
              {cookieDescription}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <div className="flex gap-2">
            <Button
              onClick={() => handleConsent('all')}
              className="flex-1 h-auto min-h-10 py-2 whitespace-normal text-center leading-tight uppercase tracking-wider transition-all active:scale-[0.98]"
              style={{ ...getButtonStyle(cookie.accept_button), minWidth: 0, flexShrink: 1 }}
            >
              {acceptLabel}
            </Button>

            <Button
              onClick={() => handleConsent('necessary')}
              className="flex-1 h-auto min-h-10 py-2 whitespace-normal text-center leading-tight uppercase tracking-wider transition-all active:scale-[0.98]"
              style={{ ...getButtonStyle(cookie.decline_button), minWidth: 0, flexShrink: 1 }}
            >
              {declineLabel}
            </Button>
          </div>

          <Link href="/polityka-cookies" className="w-full text-center">
            <Button 
              variant="ghost"
              className="w-full h-8 rounded-lg text-[9px] font-bold uppercase tracking-wider opacity-60 hover:opacity-100 transition-all"
              style={{ color: cookie.text_color }}
            >
              {t('cookie.policy')}
            </Button>
          </Link>
        </div>

        <button
          onClick={() => setShouldShow(false)}
          aria-label={t('cookie.close', 'Zamknij')}
          className="absolute top-2 right-2 p-1 opacity-30 hover:opacity-100 transition-opacity"
          style={{ color: cookie.text_color }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
